// PairPage — public /pair page entered via NTAG424 SDM tap-back URL.
//
// URL params (set by /v1/t/:token redirect):
//   ?picc=<14 hex>   the tag UID
//   ?ctr=<int>       SDM read counter
//   ?cmac=<16 hex>   SDM truncated MAC
//   ?token=<opaque>  routing token (kept for forward-compat)
//   ?error=<code>    set when /v1/t/:token rejected the tap (eg invalid_tag)
//
// Mobile-first single-card design. The flow is:
//   1. On mount, GET /v1/sdm/inspect to check CMAC + binding state.
//   2. If unbound + valid: show pairing-code input.
//   3. If already bound: show "已綁定" with link to LIFF officer scan.
//   4. If invalid: show error.
//   5. Manual fallback: no SDM params at all → ask user to type UID + code.
//
// Visual tokens come from the existing dark-theme design system. The
// SENTRY shield logo (`/sentry-shield.png`) anchors the top of the card.

import React, { useEffect, useMemo, useState } from "react";
import { Link2, CheckCircle2, AlertTriangle, Loader2, ExternalLink, ScanLine } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import QrScanFullscreen from "../../components/QrScanFullscreen.js";
import { resolveApiBase } from "@nexus/frontend-shared/api-client";

interface InspectResult {
  valid: boolean;
  reason?: string;
  uid?: string;
  ctr?: number;
  bound?: boolean;
  bound_to?: {
    location_id: string;
    location_label: string | null;
    tenant_id: string | null;
    site_id: string | null;
    site_name: string | null;
    bound_at: string;
  } | null;
}

interface PairResult {
  ok: true;
  uid: string;
  location_id: string;
  location_label: string;
  site_id: string;
  site_name: string | null;
  secured: boolean;
}

const API_BASE = resolveApiBase();

const ERROR_LABEL: Record<string, string> = {
  invalid_tag:        "標籤資料格式錯誤，請重新感應或聯絡管理員。",
  uid_format_invalid: "標籤 UID 格式不正確（需 14 個十六進位字元）。",
  code_format_invalid: "配對碼格式錯誤（需 8 碼，僅含 A–Z 與 2–9，不含 I/O/0/1）。",
  code_invalid:       "配對碼無效或不存在。",
  code_used:          "此配對碼已被使用過，請至 dashboard 重新產生。",
  code_expired:       "配對碼已過期，請至 dashboard 重新產生。",
  proof_invalid:      "標籤驗證失敗（CMAC）。可能此標籤不是由 SENTRY 程式化的，或主金鑰不對。",
  tag_already_bound:  "此標籤已綁定其他地點。請先至 dashboard 解除原綁定，再進行新配對。",
  location_not_active: "目標地點已退役，無法配對。",
  tenant_unavailable: "後端服務暫時無法處理此單位的綁定，請稍後再試。",
};

export default function PairPage(): React.ReactElement {
  const [params] = useSearchParams();
  const sdm = useMemo(() => ({
    picc: params.get("picc")?.toUpperCase() ?? "",
    ctr: params.get("ctr") ?? "",
    cmac: params.get("cmac")?.toUpperCase() ?? "",
    token: params.get("token") ?? "",
  }), [params]);
  const initialError = params.get("error");

  const [phase, setPhase] = useState<"loading" | "manual" | "form" | "bound" | "invalid" | "submitting" | "done">(
    initialError ? "invalid" : (sdm.picc && sdm.cmac && sdm.ctr ? "loading" : "manual"),
  );
  const [inspect, setInspect] = useState<InspectResult | null>(null);
  const [manualUid, setManualUid] = useState("");
  const [code, setCode] = useState("");
  const [errMsg, setErrMsg] = useState<string | null>(initialError ? (ERROR_LABEL[initialError] ?? initialError) : null);
  const [success, setSuccess] = useState<PairResult | null>(null);

  useEffect(() => {
    if (phase !== "loading") return;
    const qs = new URLSearchParams({ picc: sdm.picc, ctr: sdm.ctr, cmac: sdm.cmac, token: sdm.token });
    fetch(`${API_BASE}/v1/sdm/inspect?${qs}`).then(async (r) => {
      const body = await r.json().catch(() => ({})) as InspectResult;
      setInspect(body);
      if (!body.valid) {
        setErrMsg(body.reason ? `驗證失敗：${body.reason}` : "標籤資料無效。");
        setPhase("invalid");
        return;
      }
      if (body.bound) { setPhase("bound"); return; }
      setPhase("form");
    }).catch(() => {
      setErrMsg("無法連線到伺服器，請檢查網路後再試。");
      setPhase("invalid");
    });
  }, [phase, sdm.picc, sdm.ctr, sdm.cmac, sdm.token]);

  const submit = async () => {
    setPhase("submitting"); setErrMsg(null);
    const tagUid = sdm.picc || manualUid.trim().toUpperCase();
    const body: Record<string, unknown> = {
      tag_uid: tagUid,
      pairing_code: code.trim().toUpperCase(),
    };
    if (sdm.picc && sdm.cmac && sdm.ctr) {
      // sdm.ctr arrives as a HEX string in the URL (NTAG424 SDM mirror
       // writes 6 hex chars per NXP AN12196). Parse base 16 — Number()
       // would silently truncate at the first hex letter (e.g. "00000C"
       // → NaN; "000012" → 12 dec instead of 18 = 0x12).
       body.proof = { ctr: parseInt(sdm.ctr, 16), cmac: sdm.cmac, token: sdm.token };
    }
    try {
      const r = await fetch(`${API_BASE}/v1/sdm/pair`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({})) as PairResult & { error?: string };
      if (!r.ok || !("ok" in j)) {
        const code = (j as { error?: string }).error ?? "request_failed";
        setErrMsg(ERROR_LABEL[code] ?? `配對失敗：${code}`);
        setPhase(sdm.picc ? "form" : "manual");
        return;
      }
      setSuccess(j);
      setPhase("done");
    } catch (e) {
      setErrMsg("連線中斷，請檢查網路後再試。");
      setPhase(sdm.picc ? "form" : "manual");
    }
  };

  const tagUidShown = sdm.picc || manualUid;

  return (
    <div className="min-h-[100dvh] bg-bg-base text-text-primary flex flex-col items-center px-4 py-8">
      {/* Brand strip */}
      <header className="flex items-center gap-2 mb-6 mt-2">
        {/* Source SVG ships fill=#231815 (black on transparent) — invert
         *  via CSS filter so it renders pure white against the dark
         *  card without forking the asset. */}
        <img
          src="/sentry-icon.svg"
          alt="SENTRY"
          className="h-8 w-auto"
          style={{ filter: "brightness(0) invert(1)" }}
        />
        <span className="text-sm font-semibold tracking-wide">NEXUS</span>
      </header>

      <main className="w-full max-w-md card bg-bg-surface border-border p-6 space-y-5 animate-fade-in">
        <div>
          <h1 className="text-xl font-semibold tracking-tight inline-flex items-center gap-2">
            <Link2 className="h-5 w-5 text-accent" />
            綁定地點
          </h1>
          <p className="mt-1 text-xs text-text-muted leading-relaxed">
            請在 dashboard 產生 8 碼配對碼後，於下方輸入完成綁定。
          </p>
        </div>

        {/* Loading state */}
        {phase === "loading" && (
          <div className="py-6 flex flex-col items-center gap-2 text-text-muted">
            <Loader2 className="h-5 w-5 animate-spin" />
            <p className="text-xs">驗證標籤中…</p>
          </div>
        )}

        {/* Already bound — point user to LIFF officer scan */}
        {phase === "bound" && inspect?.bound_to && (
          <div className="space-y-3">
            <div className="rounded-md border border-success/40 bg-success/10 p-3 text-sm text-success">
              此標籤已綁定。請從 LINE 巡邏員 LIFF 進入掃描，不再需要重新配對。
            </div>
            <dl className="text-xs space-y-1.5">
              <div className="flex justify-between gap-2">
                <dt className="text-text-muted">案場</dt>
                <dd className="font-medium text-right truncate">{inspect.bound_to.site_name ?? "（未命名）"}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-text-muted">地點</dt>
                <dd className="font-medium text-right truncate">{inspect.bound_to.location_label ?? inspect.bound_to.location_id.slice(0, 8) + "…"}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-text-muted">UID</dt>
                <dd className="font-mono text-2xs text-right">{inspect.uid}</dd>
              </div>
            </dl>
            <a
              href="https://liff.line.me/2009960133-3WJCXPqT"
              className="btn-primary w-full justify-center"
            >
              <ExternalLink className="h-4 w-4" /> 開啟巡邏員 LIFF
            </a>
          </div>
        )}

        {/* Invalid tag / error from /t/ redirect */}
        {phase === "invalid" && (
          <div className="space-y-3">
            <div className="rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{errMsg ?? "標籤資料無效。"}</span>
            </div>
            <button
              className="btn-secondary w-full justify-center"
              onClick={() => { setPhase("manual"); setErrMsg(null); }}
            >
              改為手動輸入 UID 配對
            </button>
          </div>
        )}

        {/* Pair form (auto from SDM URL) */}
        {phase === "form" && (
          <PairForm
            uid={sdm.picc}
            uidLocked
            code={code}
            setCode={setCode}
            onSubmit={() => void submit()}
            errMsg={errMsg}
            secured={true}
          />
        )}

        {/* Pair form (manual) */}
        {phase === "manual" && (
          <PairForm
            uid={manualUid}
            uidLocked={false}
            onUidChange={setManualUid}
            code={code}
            setCode={setCode}
            onSubmit={() => void submit()}
            errMsg={errMsg}
            secured={false}
          />
        )}

        {/* Submitting */}
        {phase === "submitting" && (
          <div className="py-6 flex flex-col items-center gap-2 text-text-muted">
            <Loader2 className="h-5 w-5 animate-spin" />
            <p className="text-xs">配對中…</p>
          </div>
        )}

        {/* Success */}
        {phase === "done" && success && (
          <div className="space-y-4">
            <div className="rounded-md border border-success/40 bg-success/10 p-4 text-sm text-success flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">綁定完成</p>
                <p className="text-xs mt-1 opacity-90">此標籤已對應到下方地點，可以開始使用。</p>
              </div>
            </div>
            <dl className="text-xs space-y-1.5">
              <div className="flex justify-between gap-2">
                <dt className="text-text-muted">案場</dt>
                <dd className="font-medium text-right truncate">{success.site_name ?? "（未命名）"}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-text-muted">地點</dt>
                <dd className="font-medium text-right truncate">{success.location_label}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-text-muted">標籤 UID</dt>
                <dd className="font-mono text-2xs text-right">{success.uid}</dd>
              </div>
              {success.secured ? (
                <div className="flex justify-between gap-2">
                  <dt className="text-text-muted">驗證</dt>
                  <dd className="text-success font-medium text-right">✓ AES-CMAC 通過</dd>
                </div>
              ) : null}
            </dl>
            <p className="text-2xs text-text-muted text-center">您現在可以關閉此頁面。</p>
          </div>
        )}
      </main>

      <footer className="mt-6 text-2xs text-text-muted text-center">
        SENTRY NEXUS · 平台網址 <a href="https://nexus.sentry.red" className="text-accent">nexus.sentry.red</a>
        {tagUidShown && phase !== "done" ? <p className="mt-1 font-mono opacity-60">UID: {tagUidShown}</p> : null}
      </footer>
    </div>
  );
}

interface PairFormProps {
  uid: string;
  uidLocked: boolean;
  onUidChange?: (v: string) => void;
  code: string;
  setCode: (v: string) => void;
  onSubmit: () => void;
  errMsg: string | null;
  secured: boolean;
}

function PairForm({
  uid, uidLocked, onUidChange, code, setCode, onSubmit, errMsg, secured,
}: PairFormProps): React.ReactElement {
  const ready = /^[0-9A-F]{14}$/.test(uid) && /^[A-HJ-NP-Z2-9]{8}$/.test(code.toUpperCase());
  // QR-scan dropdown lives at the form level so the scanner overlays
  // the whole page on open (mobile-first; the form lives inside a
  // narrow card on /pair). Button next to the code input opens it;
  // jsqr decodes whatever's framed; we sanitise the payload with the
  // same regex the manual input uses (uppercase + drop I/O/0/1 + keep
  // first 8 chars) before accepting.
  const [scanOpen, setScanOpen] = useState(false);
  const handleScanResult = (raw: string): boolean => {
    const cleaned = raw.toUpperCase().replace(/[IO01]/g, "").replace(/[^A-Z2-9]/g, "").slice(0, 8);
    if (cleaned.length !== 8) return false; // keep scanning — payload wasn't a pairing code
    setCode(cleaned);
    setScanOpen(false);
    return true;
  };
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => { e.preventDefault(); if (ready) onSubmit(); }}
    >
      <div>
        <label className="block text-xs font-medium text-text-secondary mb-1.5" htmlFor="pair-uid">
          標籤 UID
        </label>
        <input
          id="pair-uid"
          className="input font-mono text-sm tracking-wider"
          value={uid}
          disabled={uidLocked}
          onChange={(e) => onUidChange?.(e.target.value.replace(/[^0-9a-fA-F]/g, "").toUpperCase().slice(0, 14))}
          placeholder="04A224B2C66380"
          autoComplete="off"
          inputMode={uidLocked ? undefined : "text"}
        />
        {!uidLocked && (
          <p className="mt-1 text-[10px] text-text-muted">
            14 位十六進位字元（0–9、A–F）。手動模式僅供測試或無 NFC 手機使用。
          </p>
        )}
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-1.5">
          <label className="block text-xs font-medium text-text-secondary" htmlFor="pair-code">
            配對碼
          </label>
          <button
            type="button"
            onClick={() => setScanOpen(true)}
            className="inline-flex items-center gap-1 text-xs text-accent hover:text-accent-hover transition-colors"
            aria-label="開啟 QR 掃描"
          >
            <ScanLine className="h-3.5 w-3.5" /> 掃描
          </button>
        </div>
        <input
          id="pair-code"
          className="input font-mono text-2xl tracking-[0.4em] text-center uppercase"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[IO01]/g, "").replace(/[^A-Z2-9]/g, "").slice(0, 8))}
          placeholder="ABCD2345"
          autoComplete="one-time-code"
          maxLength={8}
        />
        <p className="mt-1 text-[10px] text-text-muted">
          管理員在 dashboard 地點詳情產生的 8 碼，1 小時內有效。
        </p>
      </div>

      {!secured && (
        <div className="rounded border border-warning/40 bg-warning/10 p-2 text-2xs text-warning">
          手動配對：未驗證標籤是否為 SENTRY 程式化。建議在現場由管理員當面操作以避免誤綁。
        </div>
      )}

      {errMsg && (
        <div className="rounded border border-danger/40 bg-danger/10 p-2 text-xs text-danger flex items-start gap-2">
          <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
          <span>{errMsg}</span>
        </div>
      )}

      <button type="submit" className="btn-primary w-full" disabled={!ready}>
        <Link2 className="h-4 w-4" /> 完成綁定
      </button>
      {scanOpen && (
        <QrScanFullscreen
          title="掃描 dashboard 上的配對碼 QR"
          hint="把 QR 對進中央方框內，識別到就會自動填入。"
          onDecode={handleScanResult}
          onClose={() => setScanOpen(false)}
        />
      )}
    </form>
  );
}
